<template>
  <v-dialog v-model="internalShow" persistent max-width="390">
    <section class="pin-dialog" aria-label="PIN verification">
      <header class="pin-dialog__header">
        <div>
          <span class="pin-dialog__eyebrow">SECURE VERIFICATION</span>
          <h2>Enter your PIN</h2>
          <p v-if="employee && employee.name">{{ employee.name }}</p>
        </div>
        <button
          type="button"
          class="icon-button"
          aria-label="Close PIN dialog"
          :disabled="loading"
          @pointerdown.prevent="close"
        >
          <v-icon color="#94a3b8">mdi-close</v-icon>
        </button>
      </header>

      <div class="pin-dialog__body">
        <div class="pin-dots" aria-live="polite">
          <span
            v-for="i in 4"
            :key="i"
            class="pin-dot"
            :class="{ filled: i <= entered.length }"
          ></span>
        </div>

        <p class="pin-dialog__hint">
          {{ loading ? "Checking your PIN…" : "Tap your 4-digit employee PIN" }}
        </p>

        <div class="keypad" :class="{ 'keypad--loading': loading }">
          <button
            v-for="n in 9"
            :key="n"
            type="button"
            class="keypad__key"
            :disabled="loading"
            @pointerdown.prevent="addDigit(n)"
          >
            {{ n }}
          </button>
          <button
            type="button"
            class="keypad__key keypad__key--utility"
            :disabled="loading || !entered.length"
            @pointerdown.prevent="clear"
          >
            Clear
          </button>
          <button
            type="button"
            class="keypad__key"
            :disabled="loading"
            @pointerdown.prevent="addDigit(0)"
          >
            0
          </button>
          <button
            type="button"
            class="keypad__key keypad__key--utility"
            :disabled="loading || !entered.length"
            aria-label="Delete last digit"
            @pointerdown.prevent="backspace"
          >
            <v-icon color="currentColor">mdi-backspace-outline</v-icon>
          </button>
        </div>
      </div>
    </section>
  </v-dialog>
</template>

<script>
export default {
  name: "PinVerificationDialog",
  props: {
    value: { type: Boolean, required: true },
    employee: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      internalShow: this.value,
      entered: "",
      loading: false,
    };
  },
  watch: {
    value(val) {
      this.internalShow = val;
      if (!val) this.reset();
    },
    internalShow(val) {
      this.$emit("input", val);
    },
  },
  methods: {
    addDigit(n) {
      if (this.loading || this.entered.length >= 4) return;
      this.entered += n.toString();
      if (this.entered.length === 4) this.submit();
    },
    clear() {
      if (!this.loading) this.entered = "";
    },
    backspace() {
      if (!this.loading) this.entered = this.entered.slice(0, -1);
    },
    submit() {
      if (this.entered.length !== 4 || this.loading) return;
      this.loading = true;
      this.$emit("verifyPin", this.entered, this.finishVerification);
    },
    finishVerification(valid) {
      this.loading = false;
      if (valid) {
        this.close();
      } else {
        this.entered = "";
      }
    },
    reset() {
      this.entered = "";
      this.loading = false;
    },
    close() {
      if (this.loading) return;
      this.reset();
      this.internalShow = false;
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.pin-dialog {
  display: block;
  overflow: hidden;
  color: #f8fafc;
  background: #0b1728;
  border: 1px solid rgba(56, 189, 248, 0.25);
  border-radius: 22px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
}
.pin-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 18px;
  background: linear-gradient(135deg, #10243a, #0b1728);
  border-bottom: 1px solid rgba(148, 163, 184, 0.14);
}
.pin-dialog__eyebrow {
  color: #38bdf8;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
}
.pin-dialog__header h2 {
  margin: 4px 0 0;
  font-size: 25px;
  line-height: 1.2;
}
.pin-dialog__header p {
  margin: 5px 0 0;
  color: #94a3b8;
  font-size: 14px;
}
.icon-button {
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.1);
  touch-action: manipulation;
}
.pin-dialog__body {
  padding: 24px;
}
.pin-dots {
  display: flex;
  justify-content: center;
  gap: 18px;
}
.pin-dot {
  width: 18px;
  height: 18px;
  border: 2px solid #475569;
  border-radius: 50%;
  background: transparent;
  transition: transform 120ms ease, background 120ms ease;
}
.pin-dot.filled {
  border-color: #38bdf8;
  background: #38bdf8;
  box-shadow: 0 0 18px rgba(56, 189, 248, 0.55);
  transform: scale(1.08);
}
.pin-dialog__hint {
  min-height: 21px;
  margin: 12px 0 18px;
  color: #94a3b8;
  text-align: center;
}
.keypad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.keypad__key {
  min-height: 68px;
  color: #f8fafc;
  font: inherit;
  font-size: 25px;
  font-weight: 750;
  border: 1px solid rgba(56, 189, 248, 0.24);
  border-radius: 15px;
  background: #14283e;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  touch-action: manipulation;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.keypad__key:active {
  background: #0ea5e9;
  transform: scale(0.96);
}
.keypad__key--utility {
  color: #94a3b8;
  font-size: 14px;
  background: #101f31;
}
.keypad__key:disabled {
  opacity: 0.45;
}
.keypad--loading {
  opacity: 0.72;
}
</style>
